import { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Actions from 'Actions';
import SameDayUnlimitedActions from 'actions/SameDayUnlimitedActions';
import LanguageLocaleUtils from 'utils/LanguageLocale';
import { userSelector } from 'selectors/user/userSelector';
import basketSelector from 'selectors/basket/basketSelector';
import CurrentProductUserSpecificDetailsSelector from 'selectors/page/product/currentProductUserSpecificDetails/currentProductUserSpecificDetailsSelector';
import { globalModalsSelector } from 'selectors/page/headerFooterTemplate/data/globalModals/globalModalsSelector';
import { globalModals as globalModalsUtils } from 'utils/globalModals';
import basketConstants from 'constants/Basket';
import userUtils from 'utils/User';
import urlUtils from 'utils/Url';
import analyticsConsts from 'analytics/constants';
import processEvent from 'analytics/processEvent';
import getUserSpecificProductDetails from 'services/api/profile/getUserSpecificProductDetails';
import Empty from 'constants/empty';
import SDULandingPageModalEN from 'components/GlobalModals/SDULandingPageModal/locales/SDULandingPageModal_en_US';
import SDULandingPageModalFR from 'components/GlobalModals/SDULandingPageModal/locales/SDULandingPageModal_fr_CA';

const { SDU_SERVICE_INFO } = globalModalsUtils;
const { currentProductUserSpecificDetailsSelector } = CurrentProductUserSpecificDetailsSelector;
const { isUS } = LanguageLocaleUtils;
const { BasketType } = basketConstants;
const getText = LanguageLocaleUtils.getLocalisedDataFromResource(SDULandingPageModalEN, SDULandingPageModalFR);

const SDU_SKU = 'P483900';
const SM_IMG_SIZE = 50;

// Pure helpers (no hook deps) — safe to hoist.
const redirectToBasket = () => {
    urlUtils.redirectTo('/basket');
};
const redirectToSDUHub = () => {
    urlUtils.redirectTo('/profile/MyAccount/SameDayUnlimited');
};
const fireAnalytics = skuId => {
    processEvent.process(analyticsConsts.ASYNC_PAGE_LOAD, {
        data: {
            pageType: analyticsConsts.PAGE_TYPES.PRODUCT,
            pageName: `${analyticsConsts.PAGE_TYPES.PRODUCT}:${SDU_SKU}:n/a:*`,
            eventStrings: [analyticsConsts.Event.PROD_VIEW, analyticsConsts.Event.PRODUCT_VIEW, analyticsConsts.Event.PRODUCT_PAGE_VIEW],
            productStrings: `;${skuId};;;;eVar26=${skuId}`
        }
    });
};

/**
 * useSDUProductPageProps
 *
 * Provides all Redux-derived state, dispatched actions, and locale strings
 * that SDUProductPage needs. Replaces the connect() HOC at
 * viewModel/productPage/type/digitalProduct/sduProductPage/withSDUProductPageProps.js.
 */
export default function useSDUProductPageProps() {
    const dispatch = useDispatch();

    // Redux subscriptions
    const user = useSelector(userSelector);
    const basket = useSelector(basketSelector);
    const currentProductUserSpecificDetails = useSelector(currentProductUserSpecificDetailsSelector);
    const globalModals = useSelector(globalModalsSelector);

    // Dispatched actions
    const loadContentfulContent = useCallback((...args) => dispatch(SameDayUnlimitedActions.loadContentfulContent(...args)), [dispatch]);
    const showShippingDeliveryLocationModal = useCallback(opts => dispatch(Actions.showShippingDeliveryLocationModal(opts)), [dispatch]);
    const addSDUToBasket = useCallback((...args) => dispatch(SameDayUnlimitedActions.addSDUToBasket(...args)), [dispatch]);

    // Locale strings
    const title = getText('title');
    const subscribeToSephora = getText('subscribeToSephora');
    const subHeader = getText('subHeader');
    const free30DayTrial = getText('free30DayTrial');
    const then = getText('then');
    const annually = getText('annually');
    const regionalAvailability = getText('regionalAvailability');
    const addTrialToBasket = getText('addTrialToBasket');
    const addSubscriptionToBasket = getText('addSubscriptionToBasket');
    const sameDayUnlimited = getText('sameDayUnlimited');
    const trial = getText('trial');
    const subscription = getText('subscription');
    const alreadyAddedToBasket = getText('alreadyAddedToBasket');
    const joinForOnly = getText('joinForOnly');
    const viewBasket = getText('viewBasket');
    // Locale keys resolved but consumed only in derived values below; kept commented
    // to document parity with the original HOC's static-selector list:
    //   hasBeenAdded, activateSubscription, activateYour, free30day, andSave

    // Derived values (preserving HOC combiner logic verbatim)
    const { SDUProduct } = basket;
    const { isSDUAddedToBasket } = SDUProduct || {};
    const SDUDetails = currentProductUserSpecificDetails.currentSku || {};
    const isCanada = !isUS();
    const savingsText = `${annually}*`;
    const basketType = BasketType.Standard;
    const isUserSDUInactive = user?.userSubscriptions?.length > 0 && user?.userSubscriptions[0].status?.startsWith('INACTIVE');
    const isUserTrialEligible = userUtils.isAnonymous() || (isUserSDUInactive && user?.userSubscriptions[0].isTrialEligible);
    const isUserSDUActive = !isUserSDUInactive && !userUtils.isAnonymous();
    const alreadyAddedText = isUserTrialEligible ? `${trial} ${alreadyAddedToBasket}` : `${subscription} ${alreadyAddedToBasket}`;
    const { isSamedayUnlimitedEnabled } = Sephora.configurationSettings;
    const sduGlobalModalSid = globalModals[SDU_SERVICE_INFO]?.sid || Empty.String;

    return {
        // Locale strings (only those consumed directly by the component)
        title,
        subscribeToSephora,
        subHeader,
        free30DayTrial,
        then,
        regionalAvailability,
        addTrialToBasket,
        addSubscriptionToBasket,
        sameDayUnlimited,
        joinForOnly,
        viewBasket,
        // Derived values
        SDUDetails,
        isSDUAddedToBasket,
        isUserSDUActive,
        isCanada,
        SM_IMG_SIZE,
        savingsText,
        alreadyAddedText,
        basketType,
        isUserTrialEligible,
        isSamedayUnlimitedEnabled,
        sduGlobalModalSid,
        // Static utilities exposed as props (parity with HOC output)
        SDUSku: SDU_SKU,
        getUserSpecificProductDetails,
        redirectToBasket,
        redirectToSDUHub,
        fireAnalytics,
        // Dispatched actions
        loadContentfulContent,
        showShippingDeliveryLocationModal,
        addSDUToBasket
    };
}
